import './BlobSinkConfigManager.css';

import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  CheckCircle2,
  ChevronRight,
  Clock3,
  Loader2,
  Play,
  Plus,
  RefreshCw,
  Save,
  Sparkles,
  Trash2,
  XCircle,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Markdown } from 'tiptap-markdown';

import { MatterLink } from '@/components/entity-links';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/catalyst/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useGetBlobDetailQuery } from '@/services/api/blobService';
import {
  type BlobSinkPromptPreviewMetadata,
  type BlobSinkPromptPreviewResultSchema,
  type BlobSinkPromptPreviewRunDetailsSchema,
  type BlobSinkPromptPreviewRunStatus,
  blobSinkPromptPreviewService,
} from '@/services/api/blobSinkPromptPreviewService';
import {
  type BlobSinkActionTargetSystem,
  enhancedApi,
} from '@/services/api/generated/blobSinkConfigService';

// Extend the generated type to include systems until API types are regenerated
type TargetSystem =
  | BlobSinkActionTargetSystem
  | 'NEOS'
  | 'CLIO'
  | 'SMARTADVOCATE'
  | 'LITIFY'
  | 'CASEPEER'
  | 'MYCASE'
  | 'DROPBOX'
  | 'GOOGLE_DRIVE';

interface TargetSystemInfo {
  id: TargetSystem;
  name: string;
  description: string;
  hasFileLocationPrompt: boolean;
}

// Default file-location prompt for Dropbox firms.
// Mirrors DROPBOX_DEFAULT_FILE_LOCATION_PROMPT on the backend.
const DROPBOX_DEFAULT_FILE_LOCATION_PROMPT = `Organise every document into a subfolder within the matter folder based on document type. Use the following standard subfolder names when they apply:

- Medical Records — provider records, bills, imaging, chart notes
- Correspondence — letters to/from insurance, attorneys, clients, providers
- Insurance — policy documents, declarations pages, coverage confirmations
- Legal — pleadings, motions, court filings, subpoenas
- Intake — signed retainers, engagement letters, intake questionnaires
- Photos & Evidence — accident photos, scene evidence, property damage
- Police & Incident Reports — police reports, crash reports, incident reports
- Liens — hospital liens, government liens, lien correspondence
- Settlement — demand packages, settlement agreements, releases, closing docs
- Administrative — internal notes, checklists, miscellaneous firm documents

If a document clearly fits an existing subfolder already present in the matter folder (even if the name differs slightly from the list above), prefer the existing subfolder to avoid near-duplicate folders.

File naming rules:
- Preserve the original filename when it is already descriptive.
- When the original name is generic (e.g. "document.pdf", "scan001.pdf"), rename to: YYYY-MM-DD_<Short Description>.<ext> using the document date if available, otherwise the upload date.
- Never place files directly in the matter root — always use a subfolder.`;

// Default file-location prompt for Google Drive firms.
// Mirrors GOOGLE_DRIVE_DEFAULT_FILE_LOCATION_PROMPT on the backend.
const GOOGLE_DRIVE_DEFAULT_FILE_LOCATION_PROMPT = `Organise every document into a subfolder within the matter folder based on document type. Use the following standard subfolder names when they apply:

- Medical Records — provider records, bills, imaging, chart notes
- Correspondence — letters to/from insurance, attorneys, clients, providers
- Insurance — policy documents, declarations pages, coverage confirmations
- Legal — pleadings, motions, court filings, subpoenas
- Intake — signed retainers, engagement letters, intake questionnaires
- Photos & Evidence — accident photos, scene evidence, property damage
- Police & Incident Reports — police reports, crash reports, incident reports
- Liens — hospital liens, government liens, lien correspondence
- Settlement — demand packages, settlement agreements, releases, closing docs
- Administrative — internal notes, checklists, miscellaneous firm documents

If a document clearly fits an existing subfolder already present in the matter folder (even if the name differs slightly from the list above), prefer the existing subfolder to avoid near-duplicate folders.

File naming rules:
- Preserve the original filename when it is already descriptive.
- When the original name is generic (e.g. "document.pdf", "scan001.pdf"), rename to: YYYY-MM-DD_<Short Description>.<ext> using the document date if available, otherwise the upload date.
- Never place files directly in the matter root — always use a subfolder.`;

/** Return the system-specific default prompt, if any. */
const getDefaultPromptForSystem = (system: TargetSystem): string => {
  if (system === 'DROPBOX') return DROPBOX_DEFAULT_FILE_LOCATION_PROMPT;
  if (system === 'GOOGLE_DRIVE') return GOOGLE_DRIVE_DEFAULT_FILE_LOCATION_PROMPT;
  return '';
};

const TARGET_SYSTEMS: TargetSystemInfo[] = [
  {
    id: 'FILEVINE',
    name: 'Filevine',
    description: 'Sync documents to Filevine projects',
    hasFileLocationPrompt: true,
  },
  {
    id: 'NEOS',
    name: 'NEOS',
    description: 'Sync documents to NEOS cases',
    hasFileLocationPrompt: true,
  },
  {
    id: 'CLIO',
    name: 'Clio',
    description: 'Sync documents to Clio matters',
    hasFileLocationPrompt: true,
  },
  {
    id: 'SMARTADVOCATE',
    name: 'SmartAdvocate',
    description: 'Sync documents to SmartAdvocate cases',
    hasFileLocationPrompt: true,
  },
  {
    id: 'LITIFY',
    name: 'Litify',
    description: 'Sync documents to Litify cases (Docrio)',
    hasFileLocationPrompt: true,
  },
  {
    id: 'CASEPEER',
    name: 'CASEpeer',
    description: 'Sync documents to CASEpeer cases',
    hasFileLocationPrompt: true,
  },
  {
    id: 'MYCASE',
    name: 'MyCase',
    description: 'Sync documents to MyCase cases',
    hasFileLocationPrompt: true,
  },
  {
    id: 'DROPBOX',
    name: 'Dropbox',
    description: 'Sync documents to Dropbox folders',
    hasFileLocationPrompt: true,
  },
  {
    id: 'GOOGLE_DRIVE',
    name: 'Google Drive',
    description: 'Sync documents to Google Drive folders',
    hasFileLocationPrompt: true,
  },
];

interface BlobSinkConfigCardProps {
  firmId: string;
  config: {
    id?: string;
    target_system: string;
    is_enabled: boolean;
    default_approval_mode: string;
    file_location_prompt?: string | null;
    push_window_start_time?: string | null;
    push_window_end_time?: string | null;
    push_window_timezone?: string | null;
  };
  systemInfo: TargetSystemInfo;
  isUpdating: boolean;
  isDeleting: boolean;
  isGuessing: boolean;
  onToggleEnabled: (configId: string, isEnabled: boolean) => void;
  onApprovalModeChange: (configId: string, mode: 'AUTOMATIC' | 'HUMAN') => void;
  onSavePrompt: (configId: string, prompt: string) => void;
  onSavePushWindow: (
    configId: string,
    payload: {
      push_window_start_time: string | null;
      push_window_end_time: string | null;
      push_window_timezone: string | null;
    },
  ) => void;
  onDelete: (configId: string) => void;
  onGuessConvention: (configId: string, targetSystem: TargetSystem) => void;
  onPromptApplied: () => void;
}

const TZ_FIRM_DEFAULT = '__FIRM_DEFAULT__';

const TIMEZONE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: TZ_FIRM_DEFAULT, label: 'Use firm timezone (default)' },
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'America/New_York' },
  { value: 'America/Chicago', label: 'America/Chicago' },
  { value: 'America/Denver', label: 'America/Denver' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles' },
  { value: 'America/Anchorage', label: 'America/Anchorage' },
  { value: 'Pacific/Honolulu', label: 'Pacific/Honolulu' },
];

const toInputTime = (value?: string | null): string => {
  if (!value) return '';
  // API typically returns "HH:MM:SS" but accept "HH:MM"
  return value.length >= 5 ? value.slice(0, 5) : value;
};

const toApiTime = (value: string): string => {
  // HTML time inputs normally provide "HH:MM". Backend expects a full time.
  if (value.length === 5) return `${value}:00`;
  return value;
};

const PREVIEW_SIZE_MIN = 1;
const PREVIEW_SIZE_MAX = 25;
const PREVIEW_SIZE_DEFAULT = 10;
const PREVIEW_POLL_INTERVAL_MS = 2500;

const PREVIEW_RUNNING_STATUSES: BlobSinkPromptPreviewRunStatus[] = [
  'PENDING',
  'RUNNING',
];

const clampPreviewSize = (value: number): number =>
  Math.max(PREVIEW_SIZE_MIN, Math.min(PREVIEW_SIZE_MAX, value));

const isPreviewRunning = (
  status: BlobSinkPromptPreviewRunStatus | undefined,
): boolean => Boolean(status && PREVIEW_RUNNING_STATUSES.includes(status));

const shouldPollPreviewRun = (
  run: BlobSinkPromptPreviewRunDetailsSchema | undefined,
): boolean => {
  if (!run) return false;
  if (isPreviewRunning(run.status)) return true;
  if (run.status === 'FAILED') return false;
  return run.total_count > 0 && run.processed_count < run.total_count;
};

const getRunStatusBadgeStyle = (
  status: BlobSinkPromptPreviewRunStatus,
): string => {
  if (status === 'COMPLETED') {
    return 'border-emerald-200 bg-emerald-50/90 text-emerald-700';
  }
  if (status === 'FAILED') {
    return 'border-red-200 bg-red-50/90 text-red-700';
  }
  return 'border-amber-200 bg-amber-50/90 text-amber-700';
};

const getResultStatusBadgeStyle = (
  status: BlobSinkPromptPreviewResultSchema['status'],
): string =>
  status === 'SUCCESS'
    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
    : 'border-red-200 bg-red-50 text-red-700';

const toRunProgressPercent = (processed: number, total: number): number => {
  if (total <= 0) return 0;
  return Math.min(100, Math.round((processed / total) * 100));
};

const toRunSegmentPercent = (count: number, total: number): number => {
  if (total <= 0) return 0;
  return Math.max(0, Math.min(100, (count / total) * 100));
};

const compareStrings = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { sensitivity: 'base' });

interface ParsedPreviewLocation {
  folders: string[];
  fileName: string | null;
}

const splitLocationParts = (value: string | null): string[] =>
  (value || '')
    .split(/[\\/]+/)
    .map((part) => part.trim())
    .filter(Boolean);

const looksLikeFileName = (value: string): boolean =>
  /\.[A-Za-z0-9]{2,8}$/.test(value);

const parsePreviewLocation = (
  path: string | null,
  name: string | null,
): ParsedPreviewLocation => {
  const folders = [...splitLocationParts(path)];
  const nameParts = splitLocationParts(name);

  let fileName: string | null = null;

  if (nameParts.length > 0) {
    fileName = nameParts[nameParts.length - 1] || null;
    if (nameParts.length > 1) {
      folders.push(...nameParts.slice(0, -1));
    }
  }

  if (!fileName && folders.length > 0) {
    const lastPathPart = folders[folders.length - 1];
    if (lastPathPart && looksLikeFileName(lastPathPart)) {
      fileName = lastPathPart;
      folders.pop();
    }
  }

  return {
    folders,
    fileName,
  };
};

const renderFolderHierarchy = (segments: string[]): React.ReactNode => {
  if (!segments.length) {
    return <span className="text-slate-400">—</span>;
  }

  return (
    <div className="flex flex-wrap items-center gap-1">
      {segments.map((segment, index) => (
        <React.Fragment key={`${segment}-${index}`}>
          {index > 0 ? (
            <ChevronRight className="h-3 w-3 text-slate-400" />
          ) : null}
          <span className="inline-flex items-center rounded-md border border-slate-200 bg-white px-1.5 py-0.5 text-[11px] font-medium text-slate-700">
            {segment}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
};

const formatConfidence = (value: number | null): string => {
  if (value === null) return '—';
  return `${Math.round(value * 100)}%`;
};

const formatRunTimestamp = (value: string): string =>
  new Date(value).toLocaleString();

const getApiErrorStatus = (error: unknown): number | null => {
  if (error && typeof error === 'object' && 'status' in error) {
    const { status } = error as { status: unknown };
    if (typeof status === 'number') return status;
  }
  return null;
};

const shortUuid = (value: string): string => value.slice(0, 8);

const SMARTADVOCATE_DIRECTION_LABELS: Record<number, string> = {
  1: 'Incoming',
  2: 'Outgoing',
};

interface PreviewMetadataChip {
  key: string;
  label: string;
  hoverText: string | null;
}

const isSmartAdvocatePreviewMetadata = (
  metadata: BlobSinkPromptPreviewMetadata,
): metadata is Extract<
  BlobSinkPromptPreviewMetadata,
  { document_direction_id: number }
> => 'document_direction_id' in metadata;

const buildPreviewMetadataChips = (
  metadata: BlobSinkPromptPreviewMetadata | null,
): PreviewMetadataChip[] => {
  if (!metadata) return [];

  if (isSmartAdvocatePreviewMetadata(metadata)) {
    const chips: PreviewMetadataChip[] = [];
    const directionLabel =
      SMARTADVOCATE_DIRECTION_LABELS[metadata.document_direction_id];

    if (directionLabel) {
      chips.push({
        key: 'direction',
        label: directionLabel,
        hoverText: `Direction ID: ${metadata.document_direction_id}`,
      });
    }

    if (metadata.category_name) {
      chips.push({
        key: 'category',
        label: metadata.category_name,
        hoverText:
          metadata.category_id !== null
            ? `Category ID: ${metadata.category_id}`
            : null,
      });
    }

    if (metadata.subcategory_name) {
      chips.push({
        key: 'subcategory',
        label: metadata.subcategory_name,
        hoverText:
          metadata.subcategory_id !== null
            ? `Subcategory ID: ${metadata.subcategory_id}`
            : null,
      });
    }

    if (metadata.from_contact_name) {
      chips.push({
        key: 'from-contact',
        label: `From: ${metadata.from_contact_name}`,
        hoverText:
          metadata.from_unique_contact_id !== null
            ? `Contact ID: ${metadata.from_unique_contact_id}`
            : null,
      });
    }

    if (metadata.to_contact_name) {
      chips.push({
        key: 'to-contact',
        label: `To: ${metadata.to_contact_name}`,
        hoverText:
          metadata.to_unique_contact_id !== null
            ? `Contact ID: ${metadata.to_unique_contact_id}`
            : null,
      });
    }

    return chips;
  }

  const chips: PreviewMetadataChip[] = [];

  if (metadata.document_category_title) {
    chips.push({
      key: 'document-category',
      label: metadata.document_category_title,
      hoverText: metadata.document_category_id
        ? `Category ID: ${metadata.document_category_id}`
        : null,
    });
  }

  if (metadata.doc_type_title) {
    chips.push({
      key: 'doc-type',
      label: metadata.doc_type_title,
      hoverText: metadata.doc_type_id ? `Doc Type ID: ${metadata.doc_type_id}` : null,
    });
  }

  return chips;
};

const PreviewMetadataSummary: React.FC<{
  metadata: BlobSinkPromptPreviewMetadata | null;
}> = ({ metadata }) => {
  const chips = buildPreviewMetadataChips(metadata);

  if (!metadata) {
    return <span className="text-slate-400">—</span>;
  }

  const description =
    isSmartAdvocatePreviewMetadata(metadata) && metadata.description
      ? metadata.description
      : null;

  return (
    <div className="space-y-1.5">
      {chips.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {chips.map((chip) =>
            chip.hoverText ? (
              <Tooltip key={chip.key}>
                <TooltipTrigger asChild>
                  <span className="inline-flex cursor-help items-center rounded-md border border-slate-200 bg-white px-1.5 py-0.5 text-[11px] font-medium text-slate-700">
                    {chip.label}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs">
                  {chip.hoverText}
                </TooltipContent>
              </Tooltip>
            ) : (
              <span
                key={chip.key}
                className="inline-flex items-center rounded-md border border-slate-200 bg-white px-1.5 py-0.5 text-[11px] font-medium text-slate-700"
              >
                {chip.label}
              </span>
            ),
          )}
        </div>
      ) : null}
      {description ? (
        <p className="max-w-md text-[11px] leading-relaxed text-slate-500">
          {description}
        </p>
      ) : null}
    </div>
  );
};

const PreviewFileLink: React.FC<{
  matterId: string;
  blobId: string;
  fileName: string | null;
}> = ({ matterId, blobId, fileName }) => {
  const { data: blobDetail } = useGetBlobDetailQuery(
    { matterId, blobId },
    {
      skip: !matterId || !blobId,
    },
  );

  if (!fileName) {
    return <span className="text-slate-400">—</span>;
  }

  if (!blobDetail?.download_url) {
    return <span>{fileName}</span>;
  }

  return (
    <a
      href={blobDetail.download_url}
      target="_blank"
      rel="noreferrer noopener"
      className="underline decoration-slate-300 underline-offset-2 transition-colors hover:text-slate-700 hover:decoration-slate-500"
    >
      {fileName}
    </a>
  );
};

const BlobSinkConfigCard: React.FC<BlobSinkConfigCardProps> = ({
  firmId,
  config,
  systemInfo,
  isUpdating,
  isDeleting,
  isGuessing,
  onToggleEnabled,
  onApprovalModeChange,
  onSavePrompt,
  onSavePushWindow,
  onDelete,
  onGuessConvention,
  onPromptApplied,
}) => {
  const [editingPrompt, setEditingPrompt] = useState<string | undefined>(
    undefined,
  );
  const [pushWindowEnabled, setPushWindowEnabled] = useState<boolean>(
    Boolean(config.push_window_start_time && config.push_window_end_time),
  );
  const [pushWindowStart, setPushWindowStart] = useState<string>(
    toInputTime(config.push_window_start_time),
  );
  const [pushWindowEnd, setPushWindowEnd] = useState<string>(
    toInputTime(config.push_window_end_time),
  );
  const [pushWindowTimezone, setPushWindowTimezone] = useState<string>(
    config.push_window_timezone ?? TZ_FIRM_DEFAULT,
  );

  const defaultPrompt = getDefaultPromptForSystem(
    systemInfo.id as TargetSystem,
  );
  const effectivePrompt = config.file_location_prompt || defaultPrompt;

  const editor = useEditor({
    extensions: [StarterKit, Markdown],
    content: effectivePrompt,
    editable: config.is_enabled,
    onUpdate: ({ editor: editorInstance }) => {
      const content = editorInstance.storage.markdown.getMarkdown();
      setEditingPrompt(content);
    },
  });

  // Update editor content when config loads or changes
  useEffect(() => {
    if (editor) {
      const currentContent = editingPrompt ?? effectivePrompt;
      const editorContent = editor.storage.markdown.getMarkdown();
      if (editorContent !== currentContent) {
        editor.commands.setContent(currentContent || '');
      }
    }
  }, [editor, effectivePrompt, editingPrompt]);

  // Update editor editable state based on config enabled state
  useEffect(() => {
    if (editor) {
      editor.setEditable(config.is_enabled);
    }
  }, [editor, config.is_enabled]);

  // Sync push window state from server updates (e.g. after save or refetch)
  useEffect(() => {
    const enabled = Boolean(
      config.push_window_start_time && config.push_window_end_time,
    );
    setPushWindowEnabled(enabled);
    setPushWindowStart(toInputTime(config.push_window_start_time));
    setPushWindowEnd(toInputTime(config.push_window_end_time));
    setPushWindowTimezone(config.push_window_timezone ?? TZ_FIRM_DEFAULT);
  }, [
    config.push_window_start_time,
    config.push_window_end_time,
    config.push_window_timezone,
  ]);

  const configId = config.id as string;
  const [previewSize, setPreviewSize] = useState<number>(PREVIEW_SIZE_DEFAULT);
  const [activePreviewRunId, setActivePreviewRunId] = useState<string | null>(
    null,
  );

  const [startPreviewRun, { isLoading: isStartingPreviewRun }] =
    blobSinkPromptPreviewService.useFirmsBlobSinkConfigsPromptPreviewRunsCreateMutation();
  const [applyPreviewPrompt, { isLoading: isApplyingPreviewPrompt }] =
    blobSinkPromptPreviewService.useFirmsBlobSinkConfigsPromptPreviewRunsApplyMutation();
  const {
    data: latestPreviewRunData,
    error: latestPreviewRunErrorData,
    isFetching: isFetchingLatestPreviewRunData,
    refetch: refetchLatestPreviewRun,
  } = blobSinkPromptPreviewService.useFirmsBlobSinkConfigsPromptPreviewRunsLatestQuery(
    {
      firmId,
      configId,
    },
    {
      skip: !configId,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const {
    data: activePreviewRunData,
    error: activePreviewRunErrorData,
    isFetching: isFetchingActivePreviewRunData,
    refetch: refetchActivePreviewRun,
  } = blobSinkPromptPreviewService.useFirmsBlobSinkConfigsPromptPreviewRunsRetrieveQuery(
    {
      firmId,
      configId,
      runId: activePreviewRunId || '',
    },
    {
      skip: !configId || !activePreviewRunId,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const latestPreviewRun = activePreviewRunData || latestPreviewRunData;
  const previewLoadError = activePreviewRunId
    ? activePreviewRunErrorData
    : latestPreviewRunErrorData;

  const shouldPollLatestPreview =
    shouldPollPreviewRun(latestPreviewRun) ||
    Boolean(activePreviewRunId && !latestPreviewRun);

  useEffect(() => {
    if (!configId || !shouldPollLatestPreview) return undefined;

    const intervalId = window.setInterval(() => {
      if (activePreviewRunId) {
        refetchActivePreviewRun();
      } else {
        refetchLatestPreviewRun();
      }
    }, PREVIEW_POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [
    activePreviewRunId,
    configId,
    refetchActivePreviewRun,
    refetchLatestPreviewRun,
    shouldPollLatestPreview,
  ]);

  useEffect(() => {
    if (!activePreviewRunId || !activePreviewRunData) return;

    const isTerminal =
      activePreviewRunData.status === 'COMPLETED' ||
      activePreviewRunData.status === 'FAILED';
    const hasCaughtUp =
      activePreviewRunData.processed_count >= activePreviewRunData.total_count;

    if (isTerminal && (hasCaughtUp || activePreviewRunData.status === 'FAILED')) {
      setActivePreviewRunId(null);
    }
  }, [activePreviewRunData, activePreviewRunId]);

  useEffect(() => {
    if (
      activePreviewRunId &&
      getApiErrorStatus(activePreviewRunErrorData) === 404
    ) {
      setActivePreviewRunId(null);
    }
  }, [activePreviewRunErrorData, activePreviewRunId]);

  const latestPreviewErrorStatus = getApiErrorStatus(previewLoadError);
  const hasNoPreviewRuns = !activePreviewRunId && latestPreviewErrorStatus === 404;
  const hasPreviewLoadError = Boolean(previewLoadError) && !hasNoPreviewRuns;

  const activePromptForPreview = (editingPrompt ?? effectivePrompt).trim();
  const normalizedPreviewSize = clampPreviewSize(
    Number.isFinite(previewSize) ? previewSize : PREVIEW_SIZE_DEFAULT,
  );
  const canRunPreview =
    config.is_enabled &&
    Boolean(activePromptForPreview) &&
    !isStartingPreviewRun;
  const canApplyPreviewPrompt =
    latestPreviewRun?.status === 'COMPLETED' && !isApplyingPreviewPrompt;

  const currentStart = toInputTime(config.push_window_start_time);
  const currentEnd = toInputTime(config.push_window_end_time);
  const currentTz = config.push_window_timezone ?? TZ_FIRM_DEFAULT;
  const currentEnabled = Boolean(
    config.push_window_start_time && config.push_window_end_time,
  );

  const desiredEnabled = pushWindowEnabled;
  const desiredStart = desiredEnabled ? pushWindowStart : '';
  const desiredEnd = desiredEnabled ? pushWindowEnd : '';
  const desiredTz = desiredEnabled ? pushWindowTimezone : TZ_FIRM_DEFAULT;
  const desiredTzTrimmed = desiredTz.trim();

  const hasPushWindowEdits =
    currentEnabled !== desiredEnabled ||
    currentStart !== desiredStart ||
    currentEnd !== desiredEnd ||
    currentTz !== desiredTz;

  const canSavePushWindow =
    !isUpdating &&
    (!desiredEnabled || (Boolean(desiredStart) && Boolean(desiredEnd)));

  const handleSavePushWindow = () => {
    if (!canSavePushWindow) return;
    if (!desiredEnabled) {
      onSavePushWindow(configId, {
        push_window_start_time: null,
        push_window_end_time: null,
        push_window_timezone: null,
      });
      return;
    }
    onSavePushWindow(configId, {
      push_window_start_time: toApiTime(desiredStart),
      push_window_end_time: toApiTime(desiredEnd),
      push_window_timezone:
        desiredTz === TZ_FIRM_DEFAULT ? null : desiredTzTrimmed || null,
    });
  };

  const handlePreviewSizeBlur = () => {
    setPreviewSize(normalizedPreviewSize);
  };

  const handleRefreshPreview = async () => {
    if (!configId) return;

    try {
      if (activePreviewRunId) {
        await refetchActivePreviewRun();
      } else {
        await refetchLatestPreviewRun();
      }
    } catch (error) {
      if (getApiErrorStatus(error) === 404) return;
      toast.error('Failed to refresh latest preview run');
    }
  };

  const handleStartPreview = async () => {
    if (!configId) return;

    if (!activePromptForPreview) {
      toast.error('Add a prompt before running a preview');
      return;
    }

    const payload: {
      prompt: string;
      preview_size: number;
      base_run_id?: string;
    } = {
      prompt: activePromptForPreview,
      preview_size: normalizedPreviewSize,
    };

    if (latestPreviewRun?.id) {
      payload.base_run_id = latestPreviewRun.id;
    }

    try {
      const startedRun = await startPreviewRun({
        firmId,
        configId,
        createBlobSinkPromptPreviewRunSchema: payload,
      }).unwrap();
      setActivePreviewRunId(startedRun.id);
      toast.success('Prompt preview started');
      await refetchLatestPreviewRun();
    } catch (error) {
      toast.error('Failed to start prompt preview');
    }
  };

  const handleApplyPreviewPrompt = async () => {
    if (!configId || !latestPreviewRun) return;

    try {
      await applyPreviewPrompt({
        firmId,
        configId,
        runId: latestPreviewRun.id,
      }).unwrap();
      setEditingPrompt(undefined);
      toast.success('Preview prompt applied to configuration');
      onPromptApplied();
      await refetchLatestPreviewRun();
    } catch (error) {
      toast.error('Failed to apply preview prompt');
    }
  };

  const sortedPreviewRows = useMemo(() => {
    const previewResults = latestPreviewRun?.results ?? [];

    return previewResults
      .map((result) => {
        const proposedLocation = parsePreviewLocation(
          result.proposed_path,
          result.proposed_name,
        );
        const reasoningText = (
          result.reasoning ||
          result.error_message ||
          ''
        ).trim();

        return {
          result,
          proposedLocation,
          reasoningText,
          sortMatter: result.matter_id,
          sortPath: proposedLocation.folders.join('/'),
          sortFile: proposedLocation.fileName || '',
        };
      })
      .sort((a, b) => {
        const matterComparison = compareStrings(a.sortMatter, b.sortMatter);
        if (matterComparison !== 0) return matterComparison;

        const pathComparison = compareStrings(a.sortPath, b.sortPath);
        if (pathComparison !== 0) return pathComparison;

        const fileComparison = compareStrings(a.sortFile, b.sortFile);
        if (fileComparison !== 0) return fileComparison;

        return compareStrings(a.result.id, b.result.id);
      });
  }, [latestPreviewRun?.results]);
  const showLatestRun = Boolean(latestPreviewRun);
  const latestRunProgress = latestPreviewRun
    ? toRunProgressPercent(
        latestPreviewRun.processed_count,
        latestPreviewRun.total_count,
      )
    : 0;
  const latestRunSuccessfulPercent = latestPreviewRun
    ? toRunSegmentPercent(
        latestPreviewRun.successful_count,
        latestPreviewRun.total_count,
      )
    : 0;
  const latestRunFailedPercent = latestPreviewRun
    ? toRunSegmentPercent(
        latestPreviewRun.failed_count,
        latestPreviewRun.total_count,
      )
    : 0;
  const latestRunRemainingCount = latestPreviewRun
    ? Math.max(
        0,
        latestPreviewRun.total_count -
          latestPreviewRun.successful_count -
          latestPreviewRun.failed_count,
      )
    : 0;
  const latestRunRemainingPercent = latestPreviewRun
    ? toRunSegmentPercent(latestRunRemainingCount, latestPreviewRun.total_count)
    : 0;

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">{systemInfo.name}</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              {systemInfo.description}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Label
                htmlFor={`${systemInfo.id.toLowerCase()}-enabled`}
                className="text-sm"
              >
                Enabled
              </Label>
              <Switch
                color="blue"
                checked={config.is_enabled}
                onChange={(checked) => onToggleEnabled(configId, checked)}
                disabled={isUpdating}
              />
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={() => onDelete(configId)}
              disabled={isDeleting}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label>Default Approval Mode</Label>
          <Select
            value={config.default_approval_mode}
            onValueChange={(value: 'AUTOMATIC' | 'HUMAN') =>
              onApprovalModeChange(configId, value)
            }
            disabled={isUpdating || !config.is_enabled}
          >
            <SelectTrigger className="w-full max-w-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="HUMAN">Human Approval</SelectItem>
              <SelectItem value="AUTOMATIC">Automatic</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {config.default_approval_mode === 'HUMAN'
              ? 'Requires manual approval before pushing documents'
              : 'Automatically push documents without approval'}
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-4">
            <div>
              <Label>Push Window (Optional)</Label>
              <p className="text-xs text-muted-foreground">
                Restrict uploads to a daily time window (useful when the target
                system only allows one login at a time).
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Label
                htmlFor={`${systemInfo.id.toLowerCase()}-push-window`}
                className="text-sm"
              >
                Restrict
              </Label>
              <Switch
                color="blue"
                checked={pushWindowEnabled}
                onChange={(checked) => {
                  setPushWindowEnabled(checked);
                  if (checked && !pushWindowStart && !pushWindowEnd) {
                    setPushWindowStart('22:00');
                    setPushWindowEnd('06:00');
                  }
                }}
                disabled={isUpdating}
              />
            </div>
          </div>

          {pushWindowEnabled ? (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Start</Label>
                <Input
                  type="time"
                  value={pushWindowStart}
                  onChange={(e) => setPushWindowStart(e.target.value)}
                  disabled={isUpdating}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">End</Label>
                <Input
                  type="time"
                  value={pushWindowEnd}
                  onChange={(e) => setPushWindowEnd(e.target.value)}
                  disabled={isUpdating}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">
                  Timezone
                </Label>
                <Select
                  value={pushWindowTimezone}
                  onValueChange={(v) => setPushWindowTimezone(v)}
                  disabled={isUpdating}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select timezone" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : null}

          {pushWindowEnabled && (!pushWindowStart || !pushWindowEnd) ? (
            <p className="text-xs text-red-600">
              Start and end times are required.
            </p>
          ) : null}

          {hasPushWindowEdits ? (
            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={handleSavePushWindow}
                disabled={!canSavePushWindow}
              >
                <Save className="h-4 w-4 mr-1" />
                Save Window
              </Button>
            </div>
          ) : null}
        </div>

        {systemInfo.hasFileLocationPrompt && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <Label>File Location Prompt</Label>
                <p className="text-xs text-muted-foreground">
                  Instructions for the AI to determine where to place files in{' '}
                  {systemInfo.name}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  onGuessConvention(configId, systemInfo.id as TargetSystem)
                }
                disabled={!config.is_enabled || isGuessing}
              >
                {isGuessing ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4 mr-1" />
                )}
                Generate
              </Button>
            </div>
            <div
              className={`blob-sink-editor rounded-md border ${
                !config.is_enabled
                  ? 'bg-muted cursor-not-allowed opacity-50'
                  : ''
              }`}
            >
              <EditorContent
                editor={editor}
                className="min-h-[100px] p-3 font-mono text-sm"
              />
            </div>
            {editingPrompt !== undefined &&
              editingPrompt !== effectivePrompt && (
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    onClick={() => onSavePrompt(configId, editingPrompt)}
                    disabled={isUpdating}
                  >
                    <Save className="h-4 w-4 mr-1" />
                    Save Prompt
                  </Button>
                </div>
              )}

            <div className="relative overflow-hidden rounded-xl border border-slate-200/90 bg-gradient-to-b from-white via-slate-50/40 to-slate-100/50 p-4 shadow-[0_1px_2px_rgba(15,23,42,0.05),0_18px_40px_-28px_rgba(15,23,42,0.4)] space-y-4">
              <div className="absolute inset-x-4 top-0 h-px bg-gradient-to-r from-transparent via-slate-300/60 to-transparent" />

              <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                  <Label className="text-sm font-semibold text-slate-900">
                    Prompt Preview
                  </Label>
                  <p className="text-xs leading-relaxed text-slate-600">
                    Sample up to 25 matters, evaluate proposed pathing, and
                    apply only when the run quality is strong.
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 border-slate-300 bg-white/90 text-slate-700 hover:bg-white"
                  onClick={handleRefreshPreview}
                  disabled={
                    isFetchingLatestPreviewRunData || isFetchingActivePreviewRunData
                  }
                >
                  {isFetchingLatestPreviewRunData ||
                  isFetchingActivePreviewRunData ? (
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="mr-1 h-4 w-4" />
                  )}
                  Refresh
                </Button>
              </div>

              <div className="flex flex-wrap items-end gap-3 rounded-lg border border-slate-200/90 bg-white/90 p-3">
                <div className="space-y-1">
                  <Label
                    htmlFor={`${systemInfo.id.toLowerCase()}-preview-size`}
                    className="text-xs font-medium text-slate-600"
                  >
                    Preview Size
                  </Label>
                  <Input
                    id={`${systemInfo.id.toLowerCase()}-preview-size`}
                    type="number"
                    min={PREVIEW_SIZE_MIN}
                    max={PREVIEW_SIZE_MAX}
                    value={previewSize}
                    onChange={(e) => setPreviewSize(Number(e.target.value))}
                    onBlur={handlePreviewSizeBlur}
                    className="h-9 w-24 border-slate-300 bg-white text-slate-900"
                    disabled={!config.is_enabled || isStartingPreviewRun}
                  />
                </div>
                <Button
                  size="sm"
                  className="h-9 !bg-slate-900 !text-white hover:!bg-slate-800 disabled:!bg-slate-200 disabled:!text-slate-500"
                  onClick={handleStartPreview}
                  disabled={!canRunPreview}
                >
                  {isStartingPreviewRun ? (
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  ) : (
                    <Play className="mr-1 h-4 w-4" />
                  )}
                  Run Preview
                </Button>
                <p className="text-xs text-slate-500">
                  Uses current prompt text and reuses prior sample when
                  available.
                </p>
              </div>

              {hasPreviewLoadError ? (
                <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                  Failed to load the latest preview run.
                </p>
              ) : null}

              {showLatestRun && latestPreviewRun ? (
                <div className="rounded-xl border border-slate-200 bg-white/95 p-4 space-y-4 shadow-[0_14px_34px_-26px_rgba(15,23,42,0.35)]">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      {latestPreviewRun.status === 'COMPLETED' ? (
                        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      ) : null}
                      {latestPreviewRun.status === 'FAILED' ? (
                        <XCircle className="h-4 w-4 text-red-600" />
                      ) : null}
                      {latestPreviewRun.status !== 'COMPLETED' &&
                      latestPreviewRun.status !== 'FAILED' ? (
                        <Clock3 className="h-4 w-4 text-amber-600" />
                      ) : null}
                      <span
                        className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${getRunStatusBadgeStyle(
                          latestPreviewRun.status,
                        )}`}
                      >
                        {latestPreviewRun.status}
                      </span>
                      <span className="text-xs text-slate-500">
                        Run {shortUuid(latestPreviewRun.id)} •{' '}
                        {formatRunTimestamp(latestPreviewRun.created_at)}
                      </span>
                    </div>

                    <Button
                      size="sm"
                      variant="outline"
                      className="h-9 border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:border-slate-200 disabled:text-slate-400"
                      onClick={handleApplyPreviewPrompt}
                      disabled={!canApplyPreviewPrompt}
                    >
                      {isApplyingPreviewPrompt ? (
                        <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="mr-1 h-4 w-4" />
                      )}
                      Apply Prompt
                    </Button>
                  </div>

                  <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50/80 p-3">
                    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs">
                      <p className="font-medium text-slate-700">
                        {latestRunProgress}% complete •{' '}
                        {latestPreviewRun.processed_count}/
                        {latestPreviewRun.total_count} processed
                      </p>
                      <p className="text-slate-500">
                        Sample size {latestPreviewRun.preview_size}
                      </p>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-slate-200/80">
                      <div className="flex h-full w-full">
                        <div
                          className="h-full bg-emerald-500 transition-all duration-500"
                          style={{ width: `${latestRunSuccessfulPercent}%` }}
                        />
                        <div
                          className="h-full bg-red-500 transition-all duration-500"
                          style={{ width: `${latestRunFailedPercent}%` }}
                        />
                        <div
                          className="h-full bg-slate-400 transition-all duration-500"
                          style={{ width: `${latestRunRemainingPercent}%` }}
                        />
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-3 text-[11px]">
                      <span className="inline-flex items-center gap-1.5 text-emerald-700">
                        <span className="h-2 w-2 rounded-full bg-emerald-500" />
                        Successful {latestPreviewRun.successful_count}
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-red-700">
                        <span className="h-2 w-2 rounded-full bg-red-500" />
                        Failed {latestPreviewRun.failed_count}
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-slate-600">
                        <span className="h-2 w-2 rounded-full bg-slate-400" />
                        Remaining {latestRunRemainingCount}
                      </span>
                    </div>
                  </div>

                  {latestPreviewRun.error_message ? (
                    <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                      {latestPreviewRun.error_message}
                    </p>
                  ) : null}

                  {sortedPreviewRows.length > 0 ? (
                    <div className="overflow-x-auto rounded-lg border border-slate-200">
                      <table className="min-w-full text-xs text-slate-700">
                        <thead className="bg-slate-100/90">
                          <tr>
                            <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-600">
                              Matter
                            </th>
                            <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-600">
                              Proposed Path
                            </th>
                            <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-600">
                              Proposed File
                            </th>
                            <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-600">
                              Proposed Metadata
                            </th>
                            <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-600">
                              Confidence
                            </th>
                            <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-600">
                              Status
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {sortedPreviewRows.map(
                            ({ result, proposedLocation, reasoningText }) => {
                              return (
                                <tr key={result.id} className="align-top">
                                  <td className="px-3 py-2.5">
                                    <MatterLink
                                      entityId={result.matter_id}
                                      className="font-mono text-[11px] !text-slate-500 hover:!text-slate-700"
                                      side="top"
                                    >
                                      {shortUuid(result.matter_id)}
                                    </MatterLink>
                                  </td>
                                  <td className="px-3 py-2.5">
                                    {renderFolderHierarchy(
                                      proposedLocation.folders,
                                    )}
                                  </td>
                                  <td className="px-3 py-2.5 font-medium text-slate-900">
                                    <PreviewFileLink
                                      matterId={result.matter_id}
                                      blobId={result.blob_id}
                                      fileName={proposedLocation.fileName}
                                    />
                                  </td>
                                  <td className="px-3 py-2.5">
                                    <PreviewMetadataSummary
                                      metadata={result.proposed_metadata}
                                    />
                                  </td>
                                  <td className="px-3 py-2.5 font-medium text-slate-700">
                                    {reasoningText ? (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <span className="cursor-help underline decoration-dotted decoration-slate-400 underline-offset-2">
                                            {formatConfidence(
                                              result.confidence,
                                            )}
                                          </span>
                                        </TooltipTrigger>
                                        <TooltipContent
                                          side="top"
                                          className="max-w-xl whitespace-pre-wrap text-xs font-medium leading-relaxed"
                                        >
                                          {reasoningText}
                                        </TooltipContent>
                                      </Tooltip>
                                    ) : (
                                      formatConfidence(result.confidence)
                                    )}
                                  </td>
                                  <td className="px-3 py-2.5">
                                    <span
                                      className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold ${getResultStatusBadgeStyle(
                                        result.status,
                                      )}`}
                                    >
                                      {result.status}
                                    </span>
                                  </td>
                                </tr>
                              );
                            },
                          )}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500">
                      {shouldPollLatestPreview
                        ? 'Preview is running. Results will appear here automatically.'
                        : 'No preview results captured for this run.'}
                    </p>
                  )}
                </div>
              ) : null}

              {!showLatestRun && hasNoPreviewRuns ? (
                <p className="rounded-md border border-dashed border-slate-300 bg-white/80 px-3 py-2 text-xs text-slate-500">
                  No preview runs yet for this configuration.
                </p>
              ) : null}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

interface BlobSinkConfigManagerProps {
  firmId: string;
}

export const BlobSinkConfigManager: React.FC<BlobSinkConfigManagerProps> = ({
  firmId,
}) => {
  const {
    data: configs,
    isLoading,
    refetch,
  } = enhancedApi.useFirmsBlobSinkConfigsListQuery({ firmId });

  const [createConfig, { isLoading: isCreating }] =
    enhancedApi.useFirmsBlobSinkConfigsCreateMutation();
  const [updateConfig, { isLoading: isUpdating }] =
    enhancedApi.useFirmsBlobSinkConfigsPartialUpdateMutation();
  const [deleteConfig, { isLoading: isDeleting }] =
    enhancedApi.useFirmsBlobSinkConfigsDestroyMutation();
  const [guessConvention, { isLoading: isGuessing }] =
    enhancedApi.useLazyFirmsBlobSinkConfigsGuessConventionQuery();

  const [guessingConfigId, setGuessingConfigId] = useState<string | null>(null);

  const handleCreateConfig = async (targetSystem: TargetSystem) => {
    const systemInfo = TARGET_SYSTEMS.find((s) => s.id === targetSystem);
    try {
      await createConfig({
        firmId,
        createBlobSinkConfigSchema: {
          firm_id: firmId,
          // Cast to satisfy generated types until API is regenerated with NEOS
          target_system: targetSystem as BlobSinkActionTargetSystem,
          is_enabled: false,
          default_approval_mode: 'HUMAN',
          file_location_prompt: '',
        },
      }).unwrap();
      toast.success(`${systemInfo?.name} sync configuration created`);
      refetch();
    } catch (error) {
      toast.error('Failed to create configuration');
    }
  };

  const handleToggleEnabled = async (configId: string, isEnabled: boolean) => {
    try {
      await updateConfig({
        firmId,
        id: configId,
        pk: configId,
        patchedUpdateBlobSinkConfigSchema: {
          is_enabled: isEnabled,
        },
      }).unwrap();
      toast.success(
        isEnabled ? 'Document sync enabled' : 'Document sync disabled',
      );
      refetch();
    } catch (error) {
      toast.error('Failed to update configuration');
    }
  };

  const handleApprovalModeChange = async (
    configId: string,
    mode: 'AUTOMATIC' | 'HUMAN',
  ) => {
    try {
      await updateConfig({
        firmId,
        id: configId,
        pk: configId,
        patchedUpdateBlobSinkConfigSchema: {
          default_approval_mode: mode,
        },
      }).unwrap();
      toast.success('Approval mode updated');
      refetch();
    } catch (error) {
      toast.error('Failed to update approval mode');
    }
  };

  const handleSavePrompt = async (configId: string, prompt: string) => {
    try {
      await updateConfig({
        firmId,
        id: configId,
        pk: configId,
        patchedUpdateBlobSinkConfigSchema: {
          file_location_prompt: prompt,
        },
      }).unwrap();
      toast.success('File location prompt saved');
      refetch();
    } catch (error) {
      toast.error('Failed to save prompt');
    }
  };

  const handleSavePushWindow = async (
    configId: string,
    payload: {
      push_window_start_time: string | null;
      push_window_end_time: string | null;
      push_window_timezone: string | null;
    },
  ) => {
    try {
      await updateConfig({
        firmId,
        id: configId,
        pk: configId,
        patchedUpdateBlobSinkConfigSchema: payload,
      }).unwrap();
      toast.success('Push window saved');
      refetch();
    } catch (error) {
      toast.error('Failed to save push window');
    }
  };

  const handleDeleteConfig = async (configId: string) => {
    if (
      !window.confirm('Are you sure you want to delete this configuration?')
    ) {
      return;
    }
    try {
      await deleteConfig({
        firmId,
        id: configId,
        pk: configId,
      }).unwrap();
      toast.success('Configuration deleted');
      refetch();
    } catch (error) {
      toast.error('Failed to delete configuration');
    }
  };

  const handleGuessConvention = async (
    configId: string,
    targetSystem: TargetSystem,
  ) => {
    setGuessingConfigId(configId);
    try {
      const result = await guessConvention({
        firmId,
        // Cast to satisfy generated types until API is regenerated with NEOS
        targetSystem: targetSystem as 'FILEVINE',
      }).unwrap();

      // Use the AI-generated convention, or fall back to system default
      const convention =
        result.convention || getDefaultPromptForSystem(targetSystem);

      if (convention) {
        await updateConfig({
          firmId,
          id: configId,
          pk: configId,
          patchedUpdateBlobSinkConfigSchema: {
            file_location_prompt: convention,
          },
        }).unwrap();
        toast.success(
          result.convention
            ? 'Convention generated successfully'
            : `Default ${TARGET_SYSTEMS.find((s) => s.id === targetSystem)?.name ?? targetSystem} prompt applied`,
        );
        refetch();
      } else {
        toast.info('Could not determine a convention for this firm');
      }
    } catch (error) {
      toast.error('Failed to generate convention');
    } finally {
      setGuessingConfigId(null);
    }
  };

  const handlePromptApplied = () => {
    refetch();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Get configs by target system (use string key to handle type differences)
  const configsBySystem = new Map(
    configs?.map((c) => [c.target_system as string, c]) ?? [],
  );

  // Find systems that don't have a config yet
  const availableSystems = TARGET_SYSTEMS.filter(
    (s) => !configsBySystem.has(s.id),
  );

  // Get configs to display
  const existingConfigs = TARGET_SYSTEMS.filter((s) =>
    configsBySystem.has(s.id),
  ).map((s) => ({
    systemInfo: s,
    config: configsBySystem.get(s.id)!,
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Document Sync</h2>
          <p className="text-sm text-muted-foreground">
            Configure automatic document syncing to external systems
          </p>
        </div>
      </div>

      {existingConfigs.length > 0 ? (
        <div className="space-y-4">
          {existingConfigs.map(({ systemInfo, config }) => (
            <BlobSinkConfigCard
              key={config.id}
              firmId={firmId}
              config={config}
              systemInfo={systemInfo}
              isUpdating={isUpdating}
              isDeleting={isDeleting}
              isGuessing={isGuessing && guessingConfigId === config.id}
              onToggleEnabled={handleToggleEnabled}
              onApprovalModeChange={handleApprovalModeChange}
              onSavePrompt={handleSavePrompt}
              onSavePushWindow={handleSavePushWindow}
              onDelete={handleDeleteConfig}
              onGuessConvention={handleGuessConvention}
              onPromptApplied={handlePromptApplied}
            />
          ))}
        </div>
      ) : null}

      {availableSystems.length > 0 && (
        <Card className="border-dashed">
          <CardContent className="py-8">
            <div className="text-center space-y-4">
              <p className="text-sm text-muted-foreground">
                {existingConfigs.length === 0
                  ? 'No document sync configurations yet'
                  : 'Add another document sync'}
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {availableSystems.map((system) => (
                  <Button
                    key={system.id}
                    variant="outline"
                    onClick={() => handleCreateConfig(system.id)}
                    disabled={isCreating}
                  >
                    {isCreating ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Plus className="h-4 w-4 mr-2" />
                    )}
                    Add {system.name} Sync
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
